<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    trap-focus
    scroll="keep"
    aria-role="dialog"
    aria-label="ContactModale"
    :aria-modal="true"
    class="ContactModale"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <H3TitleView class="modal-card-title"
          >{{ $t("contactModale.title", [total]).toString() }}
        </H3TitleView>
      </header>
      <b-loading v-model="isSending" />
      <section class="modal-card-body">
        {{
          $t("contactModale.message", [
            sumPPCount,
            total,
            destinataireEnseignantCount,
            destinataireReferentCount,
          ]).toString()
        }}

        <div class="mt-3 mb-3">
          <label for="role-select">{{ $t("contactModale.destinataire") }}</label>
          <select id="role-select" v-model="selectedRole">
            <option value="Tous">{{ $t("contactModale.tous") }}</option>
            <option value="Enseignants">{{ $t("contactModale.enseignants") }}</option>
            <option value="Referents">{{ $t("contactModale.referents") }}</option>
          </select>
        </div>

        <InputWithValidation
          v-model="courriel.titre"
          rules="required"
          :label="$t('contactModale.courriel-title')"
          :maxlength="150"
          vid="ContactModale-titre"
        />

        <RichInputTextWithValidation
          v-model="courriel.contenu"
          rules="required"
          :label="$t('contactModale.courriel-body')"
          :maxlength="1400"
          type="textarea"
          vid="ContactModale-body"
        />
      </section>

      <footer class="modal-card-foot">
        <div class="buttons">
          <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
            {{ $t("close-generic") }}
          </div>
          <b-button
            :label="$t('contactModale.send').toString()"
            type="is-primary"
            rounded
            :disabled="isFormInvalid"
            @click="sendCourrielAndClose"
          />
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"

import RichInputTextWithValidation from "@/views/inputs/RichInputTextWithValidation.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"

import {
  CourrielContactCreateDTO,
  FilterDTO,
  PorteursProjetEmailListDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { EmailService } from "@/services/rest/email/EmailService"
import { BButton, BLoading, BModal } from "buefy"

export default defineComponent({
  name: "ContactModale",
  components: {
    RichInputTextWithValidation,
    InputWithValidation,
    H3TitleView,
    BModal,
    BButton,
    BLoading,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    filters: {
      type: Array as PropType<FilterDTO[]>,
      required: false,
      default: () => [],
    },
    destinataires: {
      type: Array as PropType<UtilisateurDTO[]>,
      required: false,
      default: () => [],
    },
    projetId: {
      type: String,
      required: false,
      default: "",
    },
    total: {
      type: [String, Number],
      required: true,
    },
    graeId: {
      type: String,
      required: false,
      default: "",
    },
  },

  emits: ["close-modale"],
  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      emailService: EmailService.INSTANCE,
      ppEmailList: new PorteursProjetEmailListDTO(),
      destinataireEnseignantCount: 0,
      destinataireReferentCount: 0,
      sumPPCount: 0,
      isSending: false,
      selectedRole: "Tous",
      courriel: new CourrielContactCreateDTO(),
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.$emit("close-modale")
        }
      },
    },
    isFormInvalid(): boolean {
      // Vérifie si le titre et le contenu du courriel sont vides
      return !this.courriel.titre || !this.courriel.contenu
    },
  },
  watch: {
    async open(): Promise<void> {
      this.ppEmailList = await this.emailService.getPPNumbersToSendTo(
        this.projetId,
        this.filters,
        this.graeId,
      )
      this.destinataireEnseignantCount = this.ppEmailList.destinataireEnseignantEmails.length
      this.destinataireReferentCount = this.ppEmailList.destinataireReferentEmails.length
      this.sumPPCount = this.destinataireEnseignantCount + this.destinataireReferentCount
    },
  },
  methods: {
    async sendCourrielAndClose(): Promise<void> {
      this.isSending = true
      this.setCourrielDestinataires()

      this.emailService
        .sendCourriel(this.projetId, this.filters, this.courriel, this.graeId)
        .then(() => {
          this.isSending = false
          this.infoReportingService.success(this.$t("contactModale.sendCourriel-ok").toString())
          this.emitCloseModale()
        })
        .catch((e) => {
          this.isSending = false
          this.infoReportingService.error(this.$t("contactModale.sendCourriel-ko").toString(), e)
        })
    },

    setCourrielDestinataires(): void {
      if (this.destinataires?.length === 0) {
        this.courriel.destinataires = []
        if (this.selectedRole === "Enseignants") {
          this.courriel.destinataires.push(...this.ppEmailList.destinataireEnseignantEmails)
        } else if (this.selectedRole === "Referents") {
          this.courriel.destinataires.push(...this.ppEmailList.destinataireReferentEmails)
        } else {
          this.courriel.destinataires.push(...this.ppEmailList.destinataireEnseignantEmails)
          this.courriel.destinataires.push(...this.ppEmailList.destinataireReferentEmails)
        }
      } else {
        const selectedDestinataires =
          this.destinataires
            ?.filter((destinataire) => {
              if (this.selectedRole === "Enseignants") {
                return destinataire.profil === PROFIL_UTILISATEUR.ENSEIGNANT
              } else if (this.selectedRole === "Referents") {
                return destinataire.profil === PROFIL_UTILISATEUR.REFERENT
              }
              return true
            })
            .map((destinataire) => destinataire.courriel) || []

        // Supprimer les courriels dupliqués
        this.courriel.destinataires = Array.from(new Set(selectedDestinataires))
      }
    },

    emitCloseModale(): void {
      this.courriel = new CourrielContactCreateDTO()
      this.$emit("close-modale")
    },
  },
})
</script>

<style lang="scss">
.title-without-margin {
  .title-view {
    margin-top: 0;
  }
}

.ContactModale {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 100%;
  }
}

label {
  color: #363636;
  display: block;
  font-size: 1rem;
  font-weight: 700;
}

select {
  align-items: center;
  border: 1px solid #989898;
  border-radius: 4px;
  box-shadow: none;
  display: inline-flex;
  font-size: 1rem;
  height: 2.5em;
  justify-content: flex-start;
  line-height: 1.5;
  padding: calc(0.5em - 1px) calc(0.75em - 1px);
  position: relative;
  vertical-align: top;
  width: 50%;
}
</style>
